import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  ArrowLeft,
  Compass,
  Star,
  Quote,
  Trash2,
  Calendar,
  Copy,
  Check,
} from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";

const FavoritesPage = () => {
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    const fetchFavorites = async () => {
      try {
        const response = await api.get("/favorites");
        setFavorites(response.data);
      } catch (error) {
        console.error("Error fetching favorites:", error);
        toast.error("Failed to load favorites");
      } finally {
        setLoading(false);
      }
    };
    fetchFavorites();
  }, []);

  const removeFavorite = async (guidanceId) => {
    try {
      await api.post(`/favorites/${guidanceId}`);
      setFavorites(favorites.filter((f) => f.guidance_id !== guidanceId));
      toast.success("Removed from favorites");
    } catch (error) {
      console.error("Error removing favorite:", error);
      toast.error("Failed to remove favorite");
    }
  };

  const copyToClipboard = async (text, id) => {
    try {
      await navigator.clipboard.writeText(`"${text}" - SoulGuide`);
      setCopiedId(id);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopiedId(null), 2000);
    } catch (error) {
      toast.error("Failed to copy");
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "#070B14" }}>
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "#070B14" }} data-testid="favorites-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#070B14]/70 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <Button
            data-testid="back-btn"
            variant="ghost"
            size="icon"
            onClick={() => navigate("/dashboard")}
            className="text-white/70 hover:text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A16207] flex items-center justify-center">
              <Compass className="w-4 h-4 text-[#070B14]" />
            </div>
            <span className="font-display text-xl text-white">Favorites</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        {/* Title */}
        <div className="mb-8 animate-fade-in-up">
          <h1 className="font-display text-3xl text-white mb-2">Your Collection</h1>
          <p className="text-white/60 font-body">
            Saved wise sayings to revisit whenever you need inspiration
          </p>
        </div>

        {favorites.length === 0 ? (
          <div className="glass-card p-8 text-center animate-fade-in-up">
            <Star className="w-12 h-12 text-[#D4AF37]/30 mx-auto mb-4" />
            <h3 className="font-display text-xl text-white mb-2">No favorites yet</h3>
            <p className="text-white/60 font-body mb-6">
              Tap the star icon on any guidance to save your favorite wise sayings
            </p>
            <Button onClick={() => navigate("/dashboard")} className="btn-gold">
              View Today's Guidance
            </Button>
          </div>
        ) : (
          <ScrollArea className="h-[calc(100vh-250px)]">
            <div className="space-y-4">
              {favorites.map((favorite, index) => (
                <div
                  key={favorite.favorite_id}
                  className="gold-accent-card p-6 animate-fade-in-up relative group"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`favorite-${index}`}
                >
                  {/* Quote Icon */}
                  <Quote className="w-6 h-6 text-[#D4AF37]/50 absolute top-4 left-4" />

                  {/* Content */}
                  <div className="pl-8">
                    <p className="font-display text-lg sm:text-xl text-white italic leading-relaxed mb-4">
                      "{favorite.wise_saying}"
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-white/50 text-sm font-body">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(favorite.date)}</span>
                      </div>

                      <div className="flex items-center gap-2">
                        {/* Copy Button */}
                        <button
                          onClick={() => copyToClipboard(favorite.wise_saying, favorite.favorite_id)}
                          className="p-2 rounded-full bg-white/5 text-white/50 hover:text-[#C4B5FD] hover:bg-[#8B5CF6]/10 transition-all opacity-0 group-hover:opacity-100"
                        >
                          {copiedId === favorite.favorite_id ? (
                            <Check className="w-4 h-4 text-green-400" />
                          ) : (
                            <Copy className="w-4 h-4" />
                          )}
                        </button>

                        {/* Delete Button */}
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <button className="p-2 rounded-full bg-white/5 text-white/50 hover:text-red-400 hover:bg-red-500/10 transition-all opacity-0 group-hover:opacity-100">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="bg-[#0A101D] border-white/10 text-white">
                            <AlertDialogHeader>
                              <AlertDialogTitle className="font-display">Remove from favorites?</AlertDialogTitle>
                              <AlertDialogDescription className="text-white/60 font-body">
                                This will remove this wise saying from your collection. You can always favorite it again later.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel className="btn-secondary">Cancel</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => removeFavorite(favorite.guidance_id)}
                                className="bg-red-500 hover:bg-red-600 text-white"
                              >
                                Remove
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </div>

                  {/* Star Badge */}
                  <div className="absolute top-4 right-4">
                    <Star className="w-5 h-5 text-[#D4AF37] fill-current" />
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}

        {/* Stats */}
        {favorites.length > 0 && (
          <div className="mt-6 text-center">
            <p className="text-white/40 text-sm font-body">
              {favorites.length} wise {favorites.length === 1 ? "saying" : "sayings"} saved
            </p>
          </div>
        )}
      </main>
    </div>
  );
};

export default FavoritesPage;
